import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import {
  exitApp,
  handleBackButton,
  interceptExternalLinks,
} from "./nativeShell";

/**
 * Activates the native-shell behaviours for the Capacitor build.
 *
 * Must be called from inside the router (it uses router state for the back
 * button). No-ops on the web, where both helpers return an empty cleanup.
 */
export function useNativeShell(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  // How many entries deep into the app the user is. The Capacitor back event
  // reports canGoBack from the native WebView's history, which never sees
  // react-router's pushState navigations, so it is false everywhere and cannot
  // answer this. Counting router transitions can: PUSH goes deeper, POP comes
  // back up, REPLACE swaps the current entry and changes nothing. The initial
  // render arrives as POP and must not drive it negative.
  const depth = useRef(0);

  useEffect(() => {
    if (navigationType === "PUSH") {
      depth.current += 1;
    } else if (navigationType === "POP") {
      depth.current = Math.max(0, depth.current - 1);
    }
  }, [location.key, navigationType]);

  useEffect(() => {
    const stopBack = handleBackButton(() => {
      if (depth.current > 0) {
        navigate(-1);
      } else {
        // At the root of the stack, Android expects back to leave the app.
        void exitApp();
      }
    });
    const stopLinks = interceptExternalLinks();
    return () => {
      stopBack();
      stopLinks();
    };
  }, [navigate]);
}
